import styled from '@emotion/styled';
import { deletePackageTemplate, savePackageTemplate } from '~/actions/packages.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import { closeModal } from '~/actions/settings-packages.actions';
import { ApplicationState } from '~/store';
import { GreyPrimaryAlternativeButton, PrimaryButton, SecondaryButton } from '~/components/Button';
import { CheckboxField } from '~/components/Checkbox/Checkbox';
import ErrorDialog from '~/components/Dialog/ErrorDialog/ErrorDialog';
import { InputField } from '~/components/Input';
import Modal from '~/components/Modal';
import { DistanceUnit } from '~/constants/distance';
import { InputSize } from '~/constants/form';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import { WeightUnit } from '~/constants/weight';
import PackageTemplateChooser from '~/pages/common/PackageTemplateChooser';
import {
  getInitialFormValues,
  PackageTemplateChooserFormValues,
} from '~/pages/common/PackageTemplateChooser/types';
import { onboardingTrackEvent } from '~/pages/Onboarding/store/onboarding.actions';
import * as React from 'react';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { Field, getFormValues, initialize, InjectedFormProps, reduxForm, reset } from 'redux-form';
import { getPackageTemplateIdsWithRules } from '~/selectors/automations.selectors';
import { getActiveParcelTemplate } from '~/selectors/packages.selectors';
import { isDeleting } from '~/selectors/parcel-template.selectors';
import { getUserConfigDefaults } from '~/selectors/user.selectors';
import { colors } from '~/styles/constants';
import ParcelTemplate, { isParcelTemplate } from '~/types/ParcelTemplate';
import { Events, track } from '~/utils/segment';
import { InjectedTranslateProps, Trans, translate } from '~/utils/translate';
import validate from './validate';
import {
  PACKAGE_TRACKING_EVENT,
  TrackingAppLocation,
  TrackingReference,
} from '~/utils/TrackingHelper/types';
import { trackingHelper } from '~/utils/TrackingHelper';
import { trackPackageUpdate } from '~/utils/TrackingHelper/utils';

interface SavePackageTemplateModalProps {
  applyNewParcelTemplateOnParcelForm?: boolean;
  onboarding?: boolean;
  dataAnalyticsContext?: {
    prefix?: string;
    sendTrackEvent?: (
      event: string,
      properties: Record<string, unknown>,
    ) => ReturnType<typeof onboardingTrackEvent>;
  };
  appLocation: TrackingAppLocation;
}

interface SavePackageTemplateState {
  packageTemplateObjectIdToDelete: Maybe<string>;
}

const ModalBody = styled(Modal.Body)`
  position: absolute;
  bottom: 66px;
  left: 0px;
  right: 0px;
  overflow: auto;
  top: 65px;
  @media screen and (min-width: ${ResponsiveBreakPoints.SmallMobile}px) {
    top: 71px;
  }
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    position: initial;
  }
`;

const ModalFooter = styled(Modal.Footer)`
  display: flex;
  justify-content: space-between;
  position: absolute;
  bottom: 0px;
  left: 0px;
  right: 0px;
  overflow: hidden;
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    position: initial;
  }
`;

const FooterButtons = styled('div')`
  :only-of-type {
    margin-left: auto;
  }
  width: 100%;
`;

const CancelButton = styled(GreyPrimaryAlternativeButton)`
  display: none;
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    display: initial;
  }
`;

const ApplyButton = styled(PrimaryButton)`
  margin-left: 0px !important;
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    width: auto;
    margin-left: 10px !important;
  }
`;

const Divider = styled('div')`
  border-bottom: 1px solid ${colors.greyBorder};
  padding: 12px;
  margin: 10px 30px 30px 0;
`;

const CheckboxContainer = styled('div')`
  margin-bottom: 30px;
`;

const InputFieldContainer = styled('div')`
  margin-bottom: 20px;
`;

type ComponentProps = SavePackageTemplateModalProps &
  InjectedTranslateProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  InjectedFormProps<SavePackageTemplateFormValues>;

export class SavePackageTemplateModal extends React.Component<
  ComponentProps,
  SavePackageTemplateState
> {
  constructor(props: ComponentProps) {
    super(props);

    this.state = {
      packageTemplateObjectIdToDelete: null,
    };
  }

  componentDidUpdate() {
    const { isInitialized, initialValues, initialize } = this.props;
    if (!isInitialized && initialValues) {
      initialize(initialValues);
    }
  }

  onClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.props.onCancel();
    this.props.resetForm();

    if (this.props.onboarding)
      this.props.onboardingTrackEvent('Onboarding - Cancelled Package Template Modal');
  };

  onDelete = (objectId?: string) => () => {
    this.setState({
      packageTemplateObjectIdToDelete: objectId,
    });
  };

  hideDeleteConfirm = () => {
    this.setState({
      packageTemplateObjectIdToDelete: null,
    });
  };

  onDeleteConfirm = () => {
    if (this.state.packageTemplateObjectIdToDelete) {
      const trackingReference = trackingHelper.getTrackingReference(
        PACKAGE_TRACKING_EVENT.name,
        this.props.appLocation,
        {
          parcelId: this.state.packageTemplateObjectIdToDelete,
          isAutoSave: false, // < -- No need from the settings page
          isQuickSelect: false, // < -- No need from the settings page
        },
      );

      trackPackageUpdate(trackingReference, PACKAGE_TRACKING_EVENT.operations.SAVE_ATTEMPTED, {
        parcelId: this.state.packageTemplateObjectIdToDelete,
      });
      trackPackageUpdate(trackingReference, PACKAGE_TRACKING_EVENT.operations.DELETED, {
        parcelId: this.state.packageTemplateObjectIdToDelete,
      });

      this.props.delete(this.state.packageTemplateObjectIdToDelete, trackingReference);
      this.setState({
        packageTemplateObjectIdToDelete: null,
      });
    }
  };

  renderDeleteConfirmation() {
    const { t, packageTemplateIdsWithRules } = this.props;
    const packageTemplateObjectIdToDelete = this.state.packageTemplateObjectIdToDelete;
    // remain open when delete initiated by the user, until the template is actually deleted
    const isOpen = packageTemplateObjectIdToDelete !== null || this.props.isDeleting;
    const hasAssociatedRule = packageTemplateIdsWithRules.includes(
      packageTemplateObjectIdToDelete as string,
    );
    const deleteMessage = (
      <Trans
        i18nKey={
          hasAssociatedRule ? 'packages.deleteModalTextAutomations' : 'packages.deleteModalText'
        }
      >
        <br />
      </Trans>
    );

    // message will depend on whether the package template has an associated rule with it
    return (
      isOpen && (
        <ErrorDialog
          onClose={this.hideDeleteConfirm}
          isOpen={isOpen}
          onConfirm={this.onDeleteConfirm}
          closeButtonText={t('packages.cancelButton')}
          confirmButtonText={t('packages.removeButton')}
          message={deleteMessage}
          title={t('packages.deleteModalTitle')}
        />
      )
    );
  }

  handleSubmit = (formValues: SavePackageTemplateFormValues) => {
    const { customDimensions, parcelTemplate } = formValues;

    const template =
      parcelTemplate && isParcelTemplate(parcelTemplate)
        ? parcelTemplate.carrierTemplate
        : parcelTemplate;

    const packageTemplate: ParcelTemplate = {
      objectId: this.props.parcelTemplate ? this.props.parcelTemplate.objectId : undefined,
      carrierTemplate: template,
      isDefault: formValues.isDefault,
      name: formValues.parcelTemplateName || '',
      distanceUnit: customDimensions
        ? (customDimensions.distanceUnit.value as DistanceUnit)
        : DistanceUnit.IN, // Distance unit is required even for carrier parcel templates
      // If this is not using customDimensions it is safe to default to 0.
      // The backend only cares for the values of the template used.
      height: customDimensions ? Number(customDimensions.height) : 0,
      length: customDimensions ? Number(customDimensions.length) : 0,
      width: customDimensions ? Number(customDimensions.width) : 0,
      weightUnit: formValues.weightUnit
        ? (formValues.weightUnit.value as WeightUnit)
        : WeightUnit.LB, // weight unit is always required
      weight: Number(formValues.weight),
    };

    const packageName = !template ? packageTemplate.name : template.name;
    const eventProperties = {
      'Package Name': packageName,
      'Is Custom Template': !template,
      'Is Set as Default': formValues.isDefault,
    };
    this.props.sendTrackEvent('Saved Package Template', eventProperties);
    track(Events.package_template_created, {
      package_name: packageName,
      is_default: formValues.isDefault,
    });

    const trackingReference = trackingHelper.getTrackingReference(
      PACKAGE_TRACKING_EVENT.name,
      this.props.appLocation,
      {
        parcelId: packageTemplate.objectId,
        isAutoSave: false, // < -- No need from the settings page
        isQuickSelect: false, // < -- No need from the settings page
      },
    );

    trackPackageUpdate(trackingReference, PACKAGE_TRACKING_EVENT.operations.SAVE_ATTEMPTED, {
      parcelId: packageTemplate.objectId,
    });
    if (packageTemplate.objectId) {
      trackPackageUpdate(trackingReference, PACKAGE_TRACKING_EVENT.operations.UPDATED, {
        parcelId: packageTemplate.objectId,
      });
    } else {
      trackPackageUpdate(trackingReference, PACKAGE_TRACKING_EVENT.operations.CREATED, {
        parcelId: packageTemplate.objectId,
      });
    }

    this.props.savePackageTemplate(packageTemplate, trackingReference);
  };

  renderEditTemplateModal() {
    const isNew = !this.props.parcelTemplate || !this.props.parcelTemplate.objectId;
    const title = isNew ? 'New Package Template' : 'Edit Package Template';
    const packageTemplateObjectIdToDelete = this.state.packageTemplateObjectIdToDelete;
    // open if delete not initiated, and delete is not in progress
    const isOpen = !packageTemplateObjectIdToDelete && !this.props.isDeleting;
    const tabIndex = 1;

    const templateNameField = this.props.isUsingCustomDimensions ? (
      <InputFieldContainer>
        <Field
          name="parcelTemplateName"
          component={InputField}
          placeholder="Large box with sticker"
          size={InputSize.large}
          labelPosition="top"
          label="Your Package Name"
          tabIndex={tabIndex}
          responsive
        />
      </InputFieldContainer>
    ) : null;

    return (
      isOpen && (
        <Modal.Container
          closeOnClickOutside={false}
          onClose={this.onClose}
          isOpen={this.props.isOpen}
          size="md"
        >
          <form onSubmit={this.props.handleSubmit(this.handleSubmit)}>
            <Modal.Header>{title}</Modal.Header>
            <ModalBody>
              <PackageTemplateChooser
                formName={PACKAGE_TEMPLATE_MODAL}
                editingCustomTemplate
                disableDropdown={this.props.parcelTemplate !== null}
                startingTabIndex={tabIndex}
                someOrderHasImportedWeight={false}
                shouldShowPolymailer={true}
              />
              <Divider />
              {templateNameField}
              <CheckboxContainer>
                <Field
                  component={CheckboxField}
                  label="Set as default package template for label purchase"
                  name="isDefault"
                  tabIndex={tabIndex}
                />
              </CheckboxContainer>
            </ModalBody>
            <ModalFooter>
              {isNew ? null : (
                <SecondaryButton
                  onClick={this.onDelete(this.props.parcelTemplate!.objectId)}
                  isLoading={this.props.isDeleting}
                  disabled={this.props.isDeleting}
                  useGreenSpinner
                  tabIndex={tabIndex}
                >
                  Delete Template
                </SecondaryButton>
              )}
              <FooterButtons>
                <CancelButton
                  onClick={this.onClose}
                  disabled={this.props.isDeleting}
                  tabIndex={tabIndex}
                >
                  Cancel
                </CancelButton>
                <ApplyButton
                  isLoading={this.props.submitting}
                  disabled={this.props.submitting || this.props.isDeleting}
                  type="submit"
                  tabIndex={tabIndex}
                  fullWidth
                >
                  Save Template
                </ApplyButton>
              </FooterButtons>
            </ModalFooter>
          </form>
        </Modal.Container>
      )
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderDeleteConfirmation()}
        {this.renderEditTemplateModal()}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => {
  const formValues = getFormValues(PACKAGE_TEMPLATE_MODAL)(
    state,
  ) as PackageTemplateChooserFormValues;

  const userConfigDefaults = getUserConfigDefaults(state);
  const parcelTemplate = getActiveParcelTemplate(state);

  return {
    parcelTemplate,
    isInitialized: !!formValues,
    isOpen: state.packages.savePackageTemplateModalIsOpen,
    isDeleting: isDeleting(state),
    isUsingCustomDimensions: formValues && formValues.extras.isUsingCustomDimensions,
    initialValues: {
      ...getInitialFormValues(parcelTemplate, userConfigDefaults, null),
      isDefault: parcelTemplate ? parcelTemplate.isDefault : false,
      parcelTemplateName: parcelTemplate ? parcelTemplate.name : '',
    },
    packageTemplateIdsWithRules: getPackageTemplateIdsWithRules(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: SavePackageTemplateModalProps) => {
  return {
    initialize: (initialValues) => {
      dispatch(initialize(PACKAGE_TEMPLATE_MODAL, initialValues));
    },
    onCancel: () => dispatch(closeModal()),
    resetForm: () => dispatch(reset(PACKAGE_TEMPLATE_MODAL)),
    delete: (objectId: string, trackingReference: TrackingReference) =>
      dispatch(deletePackageTemplate(objectId, trackingReference)),
    savePackageTemplate: (formData: ParcelTemplate, trackingReference: TrackingReference) =>
      dispatch(
        savePackageTemplate(
          formData,
          !!ownProps.applyNewParcelTemplateOnParcelForm,
          trackingReference,
        ),
      ),
    sendTrackEvent: (event: string, properties: Record<string, unknown> = {}) => {
      let eventName = event;
      if (!!ownProps.dataAnalyticsContext && ownProps.dataAnalyticsContext.prefix) {
        eventName = `${ownProps.dataAnalyticsContext.prefix} - ${eventName}`;
      }

      !!ownProps.dataAnalyticsContext && ownProps.dataAnalyticsContext.sendTrackEvent
        ? dispatch(ownProps.dataAnalyticsContext.sendTrackEvent(eventName, properties))
        : dispatch(sendTrackEvent(eventName, properties));
    },
    onboardingTrackEvent: (eventString: string) => dispatch(onboardingTrackEvent(eventString)),
  };
};

export const PACKAGE_TEMPLATE_MODAL = 'PACKAGE_TEMPLATE_MODAL';

export interface SavePackageTemplateFormValues extends PackageTemplateChooserFormValues {
  parcelTemplateName?: string;
  isDefault: boolean;
}

export default compose(
  translate('settings'),
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm<SavePackageTemplateFormValues>({
    validate,
    form: PACKAGE_TEMPLATE_MODAL,
    destroyOnUnmount: true,
    enableReinitialize: true,
    touchOnChange: true,
  }),
)(SavePackageTemplateModal) as React.ComponentType<SavePackageTemplateModalProps>;
